import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { normaliseMonth, toMonthKey } from '@/lib/months';
import {
  useChartOfAccounts,
  useMonthlyBalanceSheet,
  useMonthlyPnl,
} from '@/features/financials/hooks';
import { useProjectionsCapital } from '@/features/projections/hooks';
import {
  buildSheet,
  computeModel,
  monthRange,
  prevMonthKey,
  type CapitalItem,
  type CoaAccount,
  type EngineRow,
  type Model,
  type SalesCustomer,
  type SalesProduct,
  type SheetId,
  type SheetRowDef,
  type WorkbookSettings,
} from './engine';
import {
  BASE_SCENARIO,
  parseSettings,
  useBalanceMovements,
  useCellStyles,
  usePnlPlans,
  useSalesCustomers,
  useSalesProducts,
  useSalesUnits,
  useSheetCells,
  useSheetRows,
  useUpsertBalanceMovements,
  useUpsertPnlPlans,
  useUpsertSalesUnits,
  useUpsertSheetCells,
  useWorkbooks,
  type CellStyleRow,
} from './workbookHooks';

/**
 * One edit coming back from the grid: a plan value for a (row, month).
 * `value` is in DISPLAY units — dollars for currency rows, units for unit
 * rows, a fraction (0.35) for percent rows. The hook routes it to the
 * right table and converts to cents where needed.
 */
export interface PlanEdit {
  row: EngineRow;
  month: string;
  value: number;
}

export interface ProjectionModel {
  scenario: string;
  isLoading: boolean;
  error: Error | null;
  settings: WorkbookSettings;
  workbookId: string | null;
  model: Model | null;
  /** Continuous months the engine computed (⊇ visibleMonths). */
  engineMonths: string[];
  visibleMonths: string[];
  currentMonth: string;
  latestActualMonth: string | null;
  accounts: CoaAccount[];
  products: SalesProduct[];
  customers: SalesCustomer[];
  sheetRows: SheetRowDef[];
  styles: CellStyleRow[];
  buildRows: (sheet: SheetId, showAllAccounts?: boolean) => EngineRow[];
  /** Persist one or more plan edits (optimistic). */
  applyEdits: (edits: PlanEdit[]) => void;
  isSaving: boolean;
}

function toCoaAccount(a: {
  id: string;
  code: string | null;
  name: string;
  account_type: string;
  subtype: string | null;
  parent_id: string | null;
  is_active: boolean;
}): CoaAccount | null {
  const t = a.account_type;
  if (t !== 'asset' && t !== 'liability' && t !== 'equity' && t !== 'revenue' && t !== 'expense')
    return null;
  return {
    id: a.id,
    code: a.code,
    name: a.name,
    account_type: t,
    subtype: a.subtype,
    parent_id: a.parent_id,
    is_active: a.is_active,
  };
}

/**
 * Loads everything a scenario needs, runs the calculation engine once, and
 * exposes sheet builders + a single `applyEdits` entry point. Every tab in
 * the Projections workspace reads from this hook so the numbers can never
 * disagree between sheets.
 */
export function useProjectionModel(scenario: string, visibleMonths: string[]): ProjectionModel {
  const coa = useChartOfAccounts();
  const pnlActuals = useMonthlyPnl();
  const balActuals = useMonthlyBalanceSheet();
  // Capital planning items are maintained once (Capital Planning tab) and
  // shared by every scenario — they are real commitments, not assumptions.
  const capital = useProjectionsCapital(BASE_SCENARIO);
  const workbooks = useWorkbooks();
  const products = useSalesProducts(scenario);
  const customers = useSalesCustomers(scenario);
  const units = useSalesUnits(scenario);
  const movements = useBalanceMovements(scenario);
  const pnlPlans = usePnlPlans(scenario);
  const sheetRows = useSheetRows(scenario);
  const sheetCells = useSheetCells(scenario);
  const styles = useCellStyles(scenario);

  const upsertUnits = useUpsertSalesUnits(scenario);
  const upsertMovements = useUpsertBalanceMovements(scenario);
  const upsertPlans = useUpsertPnlPlans(scenario);
  const upsertCells = useUpsertSheetCells(scenario);

  const queries = [
    coa,
    pnlActuals,
    balActuals,
    capital,
    workbooks,
    products,
    customers,
    units,
    movements,
    pnlPlans,
    sheetRows,
    sheetCells,
    styles,
  ];
  const isLoading = queries.some((q) => q.isLoading);
  const error = (queries.find((q) => q.error)?.error as Error | undefined) ?? null;

  const workbook = useMemo(
    () => workbooks.data?.find((w) => w.scenario === scenario) ?? null,
    [workbooks.data, scenario],
  );
  const settings = useMemo(() => parseSettings(workbook?.settings), [workbook?.settings]);

  const accounts = useMemo(
    () => (coa.data ?? []).map(toCoaAccount).filter((a): a is CoaAccount => a !== null),
    [coa.data],
  );

  const currentMonth = toMonthKey(new Date());

  const normalisedPnlActuals = useMemo(
    () =>
      (pnlActuals.data ?? []).map((r) => ({
        account_id: r.account_id,
        month: normaliseMonth(r.month),
        amount_cents: r.amount_cents,
      })),
    [pnlActuals.data],
  );
  const normalisedBalActuals = useMemo(
    () =>
      (balActuals.data ?? []).map((r) => ({
        account_id: r.account_id,
        month: normaliseMonth(r.month),
        balance_cents: r.balance_cents,
      })),
    [balActuals.data],
  );

  const latestActualMonth = useMemo(() => {
    let latest: string | null = null;
    for (const r of normalisedPnlActuals) if (!latest || r.month > latest) latest = r.month;
    for (const r of normalisedBalActuals) if (!latest || r.month > latest) latest = r.month;
    return latest;
  }, [normalisedPnlActuals, normalisedBalActuals]);

  /**
   * The engine needs a continuous range that (a) covers every visible month
   * and (b) reaches back to the month after the latest QuickBooks close so
   * the cash chain is anchored on a real bank balance rather than a guess.
   */
  const engineMonths = useMemo(() => {
    if (visibleMonths.length === 0) return [];
    const first = visibleMonths[0];
    const last = visibleMonths[visibleMonths.length - 1];
    let start = first;
    if (latestActualMonth) {
      const anchor = nextMonthKey(latestActualMonth);
      if (anchor < start) start = anchor;
      // Also make sure at least one closed month is in range so the UI can
      // show the most recent actual beside the plan.
      if (latestActualMonth < start) start = latestActualMonth;
    }
    // Hard cap so a stale sync years back can't blow up the range.
    const capped = monthRange(start, last);
    return capped.length > 72 ? capped.slice(capped.length - 72) : capped;
  }, [visibleMonths, latestActualMonth]);

  const engineProducts = useMemo<SalesProduct[]>(
    () =>
      (products.data ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        revenue_account_id: p.revenue_account_id,
        cogs_account_id: p.cogs_account_id,
        price_big_cents: p.price_big_cents,
        price_small_cents: p.price_small_cents,
        price_retail_cents: p.price_retail_cents,
        unit_cogs_cents: p.unit_cogs_cents,
        wholesale_share: Number(p.wholesale_share),
        retail_share: Number(p.retail_share),
        position: p.position,
        is_active: p.is_active,
      })),
    [products.data],
  );

  const engineCustomers = useMemo<SalesCustomer[]>(
    () =>
      (customers.data ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        tier: c.tier,
        mix_override:
          c.mix_override && typeof c.mix_override === 'object' && !Array.isArray(c.mix_override)
            ? Object.fromEntries(
                Object.entries(c.mix_override as Record<string, unknown>)
                  .filter(([, v]) => typeof v === 'number')
                  .map(([k, v]) => [k, v as number]),
              )
            : null,
        position: c.position,
        is_active: c.is_active,
      })),
    [customers.data],
  );

  const engineSheetRows = useMemo<SheetRowDef[]>(
    () =>
      (sheetRows.data ?? []).map((r) => ({
        id: r.id,
        kind: r.kind,
        section: r.section,
        account_id: r.account_id,
        after_key: r.after_key,
        label: r.label,
        hidden: r.hidden,
        value_type: r.value_type,
        rollup: r.rollup === 'memo' ? 'memo' : 'plan',
      })),
    [sheetRows.data],
  );

  const capitalItems = useMemo<CapitalItem[]>(
    () =>
      (capital.data ?? []).map((c) => ({
        txn_date: c.txn_date,
        amount_cents: c.amount_cents,
        direction: c.direction,
        description: c.description,
      })),
    [capital.data],
  );

  const model = useMemo<Model | null>(() => {
    if (isLoading || engineMonths.length === 0 || accounts.length === 0) return null;
    return computeModel({
      months: engineMonths,
      currentMonth,
      accounts,
      pnlActuals: normalisedPnlActuals,
      balanceActuals: normalisedBalActuals,
      pnlPlans: (pnlPlans.data ?? []).map((r) => ({
        account_id: r.account_id,
        month: normaliseMonth(r.month),
        amount_cents: r.amount_cents,
      })),
      balanceMovements: (movements.data ?? []).map((r) => ({
        account_id: r.account_id,
        month: normaliseMonth(r.month),
        delta_cents: r.delta_cents,
      })),
      products: engineProducts,
      customers: engineCustomers,
      units: (units.data ?? []).map((u) => ({
        customer_id: u.customer_id,
        month: normaliseMonth(u.month),
        units: Number(u.units),
      })),
      sheetRows: engineSheetRows,
      sheetCells: (sheetCells.data ?? []).map((c) => ({
        row_id: c.row_id,
        month: normaliseMonth(c.month),
        value: Number(c.value),
      })),
      capitalItems,
      settings,
    });
  }, [
    isLoading,
    engineMonths,
    currentMonth,
    accounts,
    normalisedPnlActuals,
    normalisedBalActuals,
    pnlPlans.data,
    movements.data,
    engineProducts,
    engineCustomers,
    units.data,
    engineSheetRows,
    sheetCells.data,
    capitalItems,
    settings,
  ]);

  const buildRows = useCallback(
    (sheet: SheetId, showAllAccounts = false): EngineRow[] => {
      if (!model) return [];
      return buildSheet(model, {
        sheet,
        visibleMonths,
        sheetRows: engineSheetRows,
        products: engineProducts,
        customers: engineCustomers,
        showAllAccounts,
      });
    },
    [model, visibleMonths, engineSheetRows, engineProducts, engineCustomers],
  );

  const applyEdits = useCallback(
    (edits: PlanEdit[]) => {
      const unitCells: Array<{ customer_id: string; month: string; units: number }> = [];
      const moveCells: Array<{ account_id: string; month: string; delta_cents: number }> = [];
      const planCells: Array<{ account_id: string; month: string; amount_cents: number }> = [];
      const sheetCellsOut: Array<{ row_id: string; month: string; value: number }> = [];
      for (const e of edits) {
        const { row, month, value } = e;
        if (!row.editablePlan) continue;
        const v = Number.isFinite(value) ? value : 0;
        if (row.kind === 'customer' && row.customerId) {
          unitCells.push({ customer_id: row.customerId, month, units: Math.max(0, v) });
        } else if (row.kind === 'custom' && row.rowId) {
          sheetCellsOut.push({ row_id: row.rowId, month, value: v });
        } else if (row.kind === 'account' && row.accountId && row.effectSign) {
          // Grid shows cash effect; the table stores the balance delta.
          moveCells.push({
            account_id: row.accountId,
            month,
            delta_cents: Math.round(v * 100) * row.effectSign,
          });
        } else if (row.kind === 'account' && row.accountId) {
          planCells.push({ account_id: row.accountId, month, amount_cents: Math.round(v * 100) });
        } else if (row.kind === 'header' && row.accountId) {
          // Header shows the rollup; store what the parent itself must carry
          // for the rollup to equal the typed value. Typing 0 (or Delete)
          // clears the parent's own line rather than offsetting the children.
          const i = engineMonths.indexOf(month);
          const offset = v === 0 ? 0 : (row.planOffset?.[i] ?? 0);
          planCells.push({
            account_id: row.accountId,
            month,
            amount_cents: Math.round(v * 100) - offset,
          });
        }
      }
      const onError = (err: unknown) =>
        toast.error(err instanceof Error ? err.message : 'Could not save projection');
      if (unitCells.length) upsertUnits.mutate(unitCells, { onError });
      if (moveCells.length) upsertMovements.mutate(moveCells, { onError });
      if (planCells.length) upsertPlans.mutate(planCells, { onError });
      if (sheetCellsOut.length) upsertCells.mutate(sheetCellsOut, { onError });
    },
    [upsertUnits, upsertMovements, upsertPlans, upsertCells, engineMonths],
  );

  return {
    scenario,
    isLoading,
    error,
    settings,
    workbookId: workbook?.id ?? null,
    model,
    engineMonths,
    visibleMonths,
    currentMonth,
    latestActualMonth,
    accounts,
    products: engineProducts,
    customers: engineCustomers,
    sheetRows: engineSheetRows,
    styles: styles.data ?? [],
    buildRows,
    applyEdits,
    isSaving:
      upsertUnits.isPending ||
      upsertMovements.isPending ||
      upsertPlans.isPending ||
      upsertCells.isPending,
  };
}

export function nextMonthKey(month: string): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  const d = new Date(Date.UTC(y, m, 1));
  return toMonthKey(d);
}

export { prevMonthKey };
